import React from "react";
import {
  Settings,
  Clock,
  Monitor,
  Link,
  Check,
  Target,
  Users,
  TrendingUp,
  Globe,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";
import Image from "../Assets/2 Phon seide views Mockups 2 (1) (1).png";
import Image1 from "../Assets/QR Code (1).png";


const TrackTimeSection = ({ isDarkMode = false }) => {
  const trackFeatures = [
    "Yellow Verification Tick",
    "Broadcast Message to Followers",
  ];

  const visionPoints = [
    {
      icon: <Globe className="w-6 h-6 text-black" />,
      title: "Unify India's Construction Ecosystem",
      description:
        "Bring every stakeholder from labor to leadership onto one digital platform.",
    },
    {
      icon: <Users className="w-6 h-6 text-black" />,
      title: "Empower Every Professional",
      description:
        "Help skilled workers & experts showcase their talents, find work, & grow their careers with dignity.",
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-black" />,
      title: "Fuel Economic Mobility Through Construction",
      description:
        "Enable upward mobility for blue-collar workers by connecting them to better projects & pay.",
    },
    {
      icon: <Target className="w-6 h-6 text-black" />,
      title: "Digitize Construction Networking",
      description:
        "Replace fragmented, outdated systems with smart, location-based digital connections.",
    },
    {
      icon: <MapPin className="w-6 h-6 text-black" />,
      title: "Bridge the Urban Rural Skill Divide",
      description:
        "Give rural talent equal access to opportunities, mentorship, & markets across India.",
    },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      className={`font-['Poppins'] ${isDarkMode ? "bg-black" : "bg-white"}`}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {/* Track Time From Anywhere Section */}
      <section
        className={`pt-22 lg:pt-30 relative overflow-hidden ${
          isDarkMode ? "bg-black" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2
              className={`text-4xl sm:text-5xl lg:text-6xl font-semibold mb-8 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Upgrade to Prime
            </h2>
            <p
              className={`text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Get verified, & unlock exclusive features to
              accelerate your  career.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center min-h-4/5 py-16">
            {/* Left Side - Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className="flex justify-center lg:justify-end"
            >
              <motion.img
                style={{ width: 500, height: 500 }}
                src={Image1}
                alt="Homeegram Community"
                className="max-w-full h-auto object-contain"
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                whileHover={{ scale: 1.02 }}
              />
            </motion.div>

            {/* Right Side - Subscription Card Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center lg:justify-start"
            >
              <div className="max-w-md w-full">
                <motion.div
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className={`rounded-2xl shadow-lg border overflow-hidden ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-100"
                  }`}
                >
                  {/* Header */}
                  <div className="p-6 pb-4">
                    <h3
                      className={`text-xl font-semibold mb-1 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Experience Exclusive
                    </h3>
                    <div className="flex items-center gap-2">
                      <h2
                        className={`text-2xl font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Homeegram
                      </h2>
                      <motion.span
                        className="bg-[#FEF200] text-black text-sm font-semibold px-3 py-1 rounded-full"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        Prime
                      </motion.span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="px-6 pb-4">
                    <h4
                      className={`text-sm font-semibold mb-4 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Access to Features
                    </h4>
                    <div className="space-y-3">
                      {trackFeatures.map((feature, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center gap-3"
                        >
                          <motion.div
                            whileHover={{ rotate: 360, scale: 1.2 }}
                            transition={{ duration: 0.5 }}
                            className="w-6 h-6 text-black bg-[#FEF200] rounded-full flex items-center justify-center flex-shrink-0"
                          >
                            <Check className="w-4 h-4 text-black" />
                          </motion.div>
                          <span
                            className={`text-sm ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div
                    className={`mx-6 border-t ${
                      isDarkMode ? "border-gray-600" : "border-gray-200"
                    }`}
                  ></div>

                  {/* Terms */}
                  <div className="px-6 py-4">
                    <h4
                      className={`text-sm font-semibold mb-2 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Terms & Conditions
                    </h4>
                    <p
                      className={`text-xs leading-relaxed ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      We always respect our Users Trust.
                      <br />
                      You can only activate Subscription Plan after completing
                      the Verification Procedure.
                    </p>
                  </div>

                  {/* Pricing */}
                  <div className="px-6 pb-6">
               

                    <div>
                      <div
                        className={`text-lg font-semibold mb-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Launching Offer : 66% Off
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full bg-[#FEF200] hover:bg-yellow-500 text-black font-semibold py-3 px-6 rounded-full transition-colors duration-200"
                      >
                        Rs. 999/ 3 Month
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section
        id="vision"
        className={`py-6 lg:py-12 relative overflow-hidden ${
          isDarkMode ? "bg-black" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Content */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
              >
                <h2
                  className={`text-4xl sm:text-5xl font-semibold mb-6 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Our Vision
                </h2>
                <p
                  className={`text-lg leading-relaxed mb-8 ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  We envision a construction industry where every
                  professional regardless of background or location has equal
                  access to opportunities, recognition, & growth.
                </p>
              </motion.div>

              {/* Vision Points */}
              <div className="space-y-6">
                {visionPoints.map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, x: 5 }}
                    className={`flex items-start space-x-4 p-4 rounded-xl transition-all duration-300 ${
                      isDarkMode
                        ? "bg-gray-900 hover:bg-gray-800"
                        : "bg-gray-50 hover:bg-white hover:shadow-md"
                    }`}
                  >
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDarkMode
                          ? "bg-[#FEF200] text-black"
                          : "bg-[#FEF200] text-white"
                      }`}
                    >
                      {point.icon}
                    </motion.div>
                    <div className="flex-1">
                      <h3
                        className={`text-lg font-semibold mb-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {point.title}
                      </h3>
                      <p
                        className={`leading-relaxed ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {point.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              style={{ width: 500, height: 500 }}
              src={Image}
              alt="Homeegram Community"
              className="max-w-full h-auto object-contain"
              animate={{ y: [0, -10, 0] }}
              whileHover={{ scale: 1.02 }}
            />
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <motion.button
          onClick={scrollToTop}
          className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
            isDarkMode
              ? "bg-gray-800 hover:bg-gray-700 text-white"
              : "bg-black hover:bg-gray-800 text-white"
          }`}
          whileHover={{ scale: 1.1, y: -3 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 }}
        >
          <svg
            className="w-6 h-6 text-black"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 15l7-7 7 7"
            />
          </svg>
        </motion.button>
      </div>

      <style jsx>{`
        .h-128 {
          height: 32rem;
        }
      `}</style>
    </div>
  );
};

export default TrackTimeSection;
